import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Lock, Leaf, Zap, ArrowRight, MessageCircle } from 'lucide-react';

const HERO_IMG = "https://images.pexels.com/photos/16116476/pexels-photo-16116476.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200";

const GEO_REDIRECT_KEY = 'stillerom_geo_redirected';
const LANG_KEY = 'stillerom_lang';

// Decide if a fresh visitor should be sent to /en. Runs once per device.
// Skip if: user explicitly chose NO, already redirected once, or has an active session.
function shouldGeoRedirectToEn() {
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('lang') === 'no') return false;
        if (localStorage.getItem(GEO_REDIRECT_KEY) === '1') return false;
        const savedLang = localStorage.getItem(LANG_KEY);
        if (savedLang === 'no') return false;
        if (localStorage.getItem('skeptiker_token')) return false; // already a user
        const langs = navigator.languages || [navigator.language || ''];
        const primary = (langs[0] || '').toLowerCase();
        // Only redirect if browser strongly prefers English AND not Nordic
        if (primary.startsWith('en')) {
            const hasNordic = langs.some(l => /^(no|nb|nn|da|sv)/i.test(l));
            return !hasNordic;
        }
        return false;
    } catch {
        return false;
    }
}

export default function Landing() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [starting, setStarting] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('lang') === 'no') {
            try {
                localStorage.setItem(LANG_KEY, 'no');
                localStorage.setItem(GEO_REDIRECT_KEY, '1');
            } catch {}
            return;
        }
        if (shouldGeoRedirectToEn()) {
            try { localStorage.setItem(GEO_REDIRECT_KEY, '1'); } catch {}
            navigate('/en', { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startChat = async () => {
        const payload = { persona: 'samtalepartner', depth: 2, language: 'no' };
        if (!user) {
            sessionStorage.setItem('pending_session', JSON.stringify(payload));
            navigate('/auth');
            return;
        }
        setStarting(true);
        try {
            const { data } = await api.post('/chat/sessions', payload);
            navigate(`/app/samtale/${data.session.session_id}`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Kunne ikke starte samtale');
            setStarting(false);
        }
    };

    return (
        <div className="min-h-screen bg-bone text-ink" data-testid="landing-page">
            {/* Nav */}
            <header className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-sage flex items-center justify-center">
                        <Leaf className="w-5 h-5 text-bone" />
                    </div>
                    <span className="font-display font-bold text-lg tracking-tight">Stillerom</span>
                </div>
                <div className="flex items-center gap-3">
                    <Link to="/en" className="text-sm text-inkSoft hover:text-ink hidden sm:inline" data-testid="no-to-en-link" title="English version">🇺🇸 English</Link>
                    <Link to="/auth" className="text-sm text-inkSoft hover:text-ink transition-colors" data-testid="nav-login-link">Logg inn</Link>
                    <Button
                        onClick={startChat}
                        className="rounded-full bg-sage hover:bg-sage-dark text-bone px-5"
                        data-testid="nav-start-btn"
                    >
                        Start samtale
                    </Button>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-7xl mx-auto px-6 pt-10 lg:pt-20 pb-24 grid lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7 animate-fade-up">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sage/10 text-sage-dark text-xs font-medium mb-6">
                        <span className="w-1.5 h-1.5 rounded-full bg-sage animate-pulse"></span>
                        Lavterskel samtaletjeneste · Norden
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                        Noen å snakke med.<br/>
                        <span className="text-sage">Når du trenger det.</span>
                    </h1>
                    <p className="mt-6 text-inkSoft text-lg max-w-xl leading-relaxed">
                        Et sted å lufte tanker, følelser og bekymringer – på dine premisser.
                        Ingen ventelister, ingen skjemaer, ingen blikk.
                        Bare en rolig samtale, når du trenger den.
                    </p>

                    <div className="mt-8 flex flex-wrap gap-3">
                        <Button
                            onClick={startChat}
                            disabled={starting}
                            size="lg"
                            className="rounded-full bg-sage hover:bg-sage-dark text-bone px-7 h-12 text-base"
                            data-testid="hero-start-btn"
                        >
                            {starting ? 'Starter…' : <>Start samtale nå <ArrowRight className="w-4 h-4 ml-2" /></>}
                        </Button>
                    </div>

                    <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
                        <TrustItem icon={<Lock className="w-4 h-4" />} label="100% anonymt" />
                        <TrustItem icon={<Zap className="w-4 h-4" />} label="Umiddelbar tilgang" />
                        <TrustItem icon={<MessageCircle className="w-4 h-4" />} label="Nordisk språk" />
                    </div>
                </div>

                <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: '0.2s' }}>
                    <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-sm">
                        <img src={HERO_IMG} alt="Rolig nordisk landskap" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/30 via-transparent to-transparent" />
                    </div>
                    <div className="absolute -bottom-6 -left-6 bg-bone border border-sand rounded-2xl p-4 shadow-sm w-56">
                        <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-sage-soft flex items-center justify-center shrink-0">
                                <Leaf className="w-4 h-4 text-sage-dark" />
                            </div>
                            <div>
                                <p className="text-xs text-inkSoft">Samtalepartner</p>
                                <p className="text-sm text-ink leading-snug">Hva går gjennom hodet ditt akkurat nå?</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="border-t border-sand mt-12">
                <div className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-sm text-inkSoft">
                    <p>© {new Date().getFullYear()} Stillerom · Alle rettigheter reservert.</p>
                    <div className="flex items-center gap-5">
                        <Link to="/om" className="hover:text-ink" data-testid="footer-om">Om Stillerom</Link>
                        <Link to="/tilbakemelding" className="hover:text-ink" data-testid="footer-feedback">Gi tilbakemelding</Link>
                        <Link to="/personvern" className="hover:text-ink" data-testid="footer-privacy">Personvern</Link>
                        <Link to="/vilkar" className="hover:text-ink" data-testid="footer-terms">Vilkår</Link>
                        <span>Mental Helse · 116 123</span>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto px-6 pb-6 text-[11px] text-inkSoft/70">
                    Stillerom bruker en KI-assistent (OpenAI GPT-5.2) til å generere svar. Du snakker med en KI, ikke et menneske. Ikke en erstatning for terapi eller helsehjelp.
                </div>
            </footer>
        </div>
    );
}

function TrustItem({ icon, label }) {
    return (
        <div className="flex flex-col gap-2">
            <div className="w-8 h-8 rounded-full bg-bone border border-sand flex items-center justify-center text-sage-dark">
                {icon}
            </div>
            <p className="text-xs text-inkSoft leading-tight">{label}</p>
        </div>
    );
}
